import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Building2, Menu, Eye, EyeOff } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { 
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { searchAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { usePrivacy } from '../../context/PrivacyContext';
import NotificationBell from '../NotificationBell';

export const Topbar = ({ collapsed, isMobile, onMenuClick }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ customers: [], properties: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { privacy, togglePrivacy } = usePrivacy();

  // Keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults({ customers: [], properties: [] });
      return;
    }
    
    setLoading(true);
    try {
      const response = await searchAPI.search(searchQuery);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 400); // Slightly longer debounce for mobile
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleSelect = (type, id) => {
    setOpen(false);
    setQuery('');
    if (type === 'customer') {
      navigate(`/customers/${id}`);
    } else if (type === 'property') {
      navigate(`/properties/${id}`);
    }
  };

  return (
    <header 
      className={cn(
        "fixed top-0 right-0 h-16 glass z-30 flex items-center px-3 md:px-6 transition-all duration-300",
        isMobile ? "left-0" : (collapsed ? "left-16" : "left-64")
      )}
      data-testid="topbar"
    >
      {/* Mobile menu button */}
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          className="mr-2"
          onClick={onMenuClick}
        >
          <Menu className="w-5 h-5" />
        </Button>
      )}

      {/* Welcome message */}
      <div className="text-lg font-medium text-foreground">
        Welkom{user ? `, ${user.first_name}` : ''}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Presentatiemodus (admin): verberg bedragen tijdens demo's */}
      {user?.role === 'admin' && (
        <Button variant="ghost" size="icon" onClick={togglePrivacy}
          title={privacy ? 'Presentatiemodus uit: bedragen tonen' : 'Presentatiemodus aan: bedragen verbergen'}
          data-testid="privacy-toggle-btn">
          {privacy ? <EyeOff className="w-5 h-5 text-orange-500" /> : <Eye className="w-5 h-5" />}
        </Button>
      )}

      {/* Notification Bell - only for admins */}
      {user?.role === 'admin' && <NotificationBell />}

      {/* Search Dialog - activated with Cmd+K */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput 
          placeholder="Search by name, email, address, city..." 
          value={query}
          onValueChange={setQuery}
          data-testid="search-input"
        />
        <CommandList>
          <CommandEmpty>
            {loading ? 'Searching...' : 'No results found.'}
          </CommandEmpty>
          
          {results.customers.length > 0 && (
            <CommandGroup heading="Customers">
              {results.customers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  onSelect={() => handleSelect('customer', customer.id)}
                  className="cursor-pointer"
                  data-testid={`search-result-customer-${customer.id}`}
                >
                  <User className="w-4 h-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span>{customer.first_name} {customer.last_name}</span>
                    <span className="text-xs text-muted-foreground">{customer.email}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results.properties.length > 0 && (
            <CommandGroup heading="Properties">
              {results.properties.map((property) => (
                <CommandItem
                  key={property.id}
                  onSelect={() => handleSelect('property', property.id)}
                  className="cursor-pointer"
                  data-testid={`search-result-property-${property.id}`}
                >
                  <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span>{property.street} {property.number}</span>
                    <span className="text-xs text-muted-foreground">{property.postal_code} {property.city}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </header>
  );
};
